<template>
  <div class="relative" ref="container">
    <button
      type="button"
      class="inline-flex items-center gap-1.5 bg-gray-800 hover:bg-gray-700 text-gray-200 px-3 py-2 rounded-md text-sm font-medium transition-colors shadow"
      @click="toggle"
    >
      <svg
        v-if="hasToken"
        class="h-4 w-4 text-green-400"
        viewBox="0 0 20 20"
        fill="currentColor"
        aria-hidden="true"
      >
        <path
          fill-rule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clip-rule="evenodd"
        />
      </svg>
      Token API
    </button>
    <transition name="fade">
      <div
        v-if="justSaved"
        class="absolute right-0 mt-2 flex items-center gap-1.5 rounded-md bg-green-600 px-3 py-2 text-sm font-medium text-white shadow-lg z-50 whitespace-nowrap"
      >
        <svg class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path
            fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
            clip-rule="evenodd"
          />
        </svg>
        Token zapisano
      </div>
    </transition>
    <transition name="fade">
      <div
        v-if="isOpen"
        class="absolute right-0 mt-2 w-80 rounded-lg border border-gray-700 bg-gray-900 p-4 shadow-xl z-50"
      >
        <h3 class="text-sm font-semibold text-gray-200">
          Ustawienia tokenu API
        </h3>
        <p class="text-xs text-gray-400 mt-1">
          Aktualne źródło: {{ sourceLabel }}
        </p>
        <form class="mt-4 space-y-4" @submit.prevent="saveToken">
          <div>
            <label
              for="admin-token-input"
              class="block text-xs font-medium text-gray-300 uppercase tracking-wider"
            >
              Token
            </label>
            <input
              id="admin-token-input"
              v-model="tokenInput"
              type="password"
              autocomplete="off"
              spellcheck="false"
              class="mt-1 block w-full rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/60"
              placeholder="Wklej token..."
            />
          </div>
          <fieldset class="space-y-1">
            <legend class="text-xs font-medium text-gray-300 uppercase tracking-wider">
              Przechowywanie
            </legend>
            <label class="flex items-center gap-2 text-sm text-gray-200">
              <input
                v-model="persistMode"
                type="radio"
                value="session"
                class="h-3 w-3 text-blue-500 focus:ring-blue-500 border-gray-600"
              />
              Tylko bieżąca sesja (sessionStorage)
            </label>
            <label class="flex items-center gap-2 text-sm text-gray-200">
              <input
                v-model="persistMode"
                type="radio"
                value="local"
                class="h-3 w-3 text-blue-500 focus:ring-blue-500 border-gray-600"
              />
              Trwale w przeglądarce (localStorage)
            </label>
          </fieldset>
          <div class="flex justify-between items-center gap-2 pt-1">
            <button
              type="submit"
              class="inline-flex items-center justify-center rounded-md bg-blue-600 hover:bg-blue-500 px-3 py-2 text-sm font-medium text-white transition-colors"
            >
              Zapisz token
            </button>
            <button
              type="button"
              class="text-sm text-gray-400 hover:text-gray-200 underline"
              @click="clear"
            >
              Wyczyść
            </button>
          </div>
        </form>
        <p class="text-[11px] text-gray-500 mt-3">
          Token nie jest logowany ani wysyłany poza API. Haseł nie zapisujemy w
          kodzie źródłowym.
        </p>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import {
  clearAdminApiToken,
  getAdminApiTokenSource,
  setAdminApiToken,
  useAdminApiAuth,
} from '@/composables/adminApiAuth';

const tokenInput = ref('');
const persistMode = ref('local');
const isOpen = ref(false);
const container = ref(null);
const justSaved = ref(false);
let justSavedTimer = null;

const sourceLabel = computed(() => {
  const source = getAdminApiTokenSource();
  switch (source) {
    case 'session':
      return 'sessionStorage (sesja przeglądarki)';
    case 'local':
      return 'localStorage (trwale)';
    case 'env':
      return 'zmienna środowiskowa';
    default:
      return 'nie ustawiono';
  }
});

const { dismissAuthError, hasToken } = useAdminApiAuth();

const close = () => {
  isOpen.value = false;
  tokenInput.value = '';
};

const toggle = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    const source = getAdminApiTokenSource();
    persistMode.value = source === 'session' ? 'session' : 'local';
  } else {
    tokenInput.value = '';
  }
};

const onClickOutside = (event) => {
  if (!isOpen.value) {
    return;
  }
  const el = container.value;
  if (!el) {
    return;
  }
  if (!el.contains(event.target)) {
    close();
  }
};

onMounted(() => {
  document.addEventListener('click', onClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', onClickOutside);
  if (justSavedTimer) {
    clearTimeout(justSavedTimer);
  }
});

const saveToken = () => {
  if (!tokenInput.value) {
    return;
  }

  setAdminApiToken(tokenInput.value.trim(), { persist: persistMode.value });
  dismissAuthError();
  close();

  justSaved.value = true;
  if (justSavedTimer) {
    clearTimeout(justSavedTimer);
  }
  justSavedTimer = setTimeout(() => {
    justSaved.value = false;
  }, 3000);
};

const clear = () => {
  clearAdminApiToken();
  dismissAuthError();
  close();
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 120ms ease, transform 120ms ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>


